import { t } from "../../i18n";

interface SeoLengthCounterProps {
  length: number;
  limit: number;
  testId: string;
}

interface SeoCountedFieldProps {
  id: string;
  label: string;
  value: string;
  limit: number;
  multiline: boolean;
  onChange: (value: string) => void;
  /** Shown under the field, typically what an empty field falls back to. */
  fallback?: JSX.Element;
}

function SeoLengthCounter({ length, limit, testId }: SeoLengthCounterProps): JSX.Element {
  // Over the limit is advice, not an error: the engine may still show the whole text.
  const overLimit = length > limit;
  return (
    <small
      className={overLimit ? "text-warning" : "text-muted"}
      data-testid={testId}
      data-over-limit={overLimit}
    >
      {length} / {limit}
      {overLimit && ` — ${t("admin.seo.over-limit", undefined, "May be cut off in Google")}`}
    </small>
  );
}

/** A labeled text field with a length counter against a recommended limit. */
export function SeoCountedField({ id, label, value, limit, multiline, onChange, fallback }: SeoCountedFieldProps): JSX.Element {
  return (
    <div className="form-group">
      <div className="d-flex justify-content-between">
        <label htmlFor={id}>{label}</label>
        <SeoLengthCounter
          length={value.length}
          limit={limit}
          testId={`${id}-counter`}
        />
      </div>
      {multiline ? (
        <textarea
          id={id}
          className="form-control"
          rows={3}
          value={value}
          data-testid={id}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <input
          id={id}
          className="form-control"
          type="text"
          value={value}
          data-testid={id}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {fallback}
    </div>
  );
}
